import React, { useState, useMemo, useCallback } from 'react';
import {
    Typography,
    Tabs,
    Table,
    Tag,
    Input,
    Select,
    Space,
    Button,
    Badge,
    Tooltip,
    Modal,
    message,
    Row,
    Col,
    Empty,
    Spin,
    Alert,
    Drawer,
} from 'antd';
import {
    FileTextOutlined,
    SearchOutlined,
    CopyOutlined,
    DeleteOutlined,
    DownloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    LockOutlined,
    SaveOutlined,
    UndoOutlined,
    ApartmentOutlined,
} from '@ant-design/icons';
import { downloadTextFile, toCsv } from '../../utils/workbook';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetTemplateTypesQuery,
    useGetTemplatesByTypeQuery,
    useDeleteTemplateTypeMutation,
    useUpdateQuestionTemplateMutation,
    useGetAdminQuestionTemplateTypesQuery,
    IQuestionTemplate,
} from '../../services/nestjs-base-api';
import { FormattedMessage, useIntl } from 'react-intl';
import { CloneTemplateTypeWizard } from '../components/CloneTemplateTypeWizard';
import { TemplateTypeRegistryPanel } from '../components/TemplateTypeRegistryPanel';

const { Title, Text } = Typography;

const SCORING_TYPE_OPTIONS = [
    'likert_7_standard',
    'likert_7_zero_based',
    'likert_7_binary',
    'likert_7_flipped',
    'likert_7_with_na_standard',
    'likert_5_standard',
    'likert_5_zero_based',
    'likert_5_binary',
    'nps_standard',
    'nps_categories',
    'rating_10_standard',
    'rating_10_normalized',
    'yes_no_standard',
    'yes_no_inverted',
    'custom',
];

// Per-template pending edits, keyed by template id. Only changed fields are stored.
interface TemplateEdit {
    text?: string;
    scoringType?: string;
    status?: 'active' | 'inactive';
}

const MODIFIED_STYLE = { borderColor: '#faad14', background: '#fffbe6' };

type SetField = (
    id: number,
    field: keyof TemplateEdit,
    value: string,
    original: string,
) => void;

// Editable cells are memoized so that typing in one row only re-renders that
// row's cell — without this the whole table (every TextArea/Select) re-renders
// on every keystroke, which is very slow with the full statement list.
const EditableTextCell = React.memo<{
    id: number;
    original: string;
    edited?: string;
    disabled: boolean;
    onChange: SetField;
}>(({ id, original, edited, disabled, onChange }) => (
    <Input.TextArea
        value={edited ?? original}
        autoSize={{ minRows: 1, maxRows: 4 }}
        disabled={disabled}
        onChange={(e) => onChange(id, 'text', e.target.value, original)}
        style={edited !== undefined ? MODIFIED_STYLE : undefined}
    />
));

const EditableScoringCell = React.memo<{
    id: number;
    original: string;
    edited?: string;
    disabled: boolean;
    onChange: SetField;
}>(({ id, original, edited, disabled, onChange }) => (
    <Select
        value={edited ?? original}
        style={{ width: '100%' }}
        status={edited !== undefined ? 'warning' : undefined}
        disabled={disabled}
        onChange={(val) => onChange(id, 'scoringType', val, original)}
        options={SCORING_TYPE_OPTIONS.map((st) => ({
            value: st,
            label: st.replace(/_/g, ' '),
        }))}
    />
));

const EditableStatusCell = React.memo<{
    id: number;
    original: string;
    edited?: string;
    disabled: boolean;
    activeLabel: string;
    inactiveLabel: string;
    onChange: SetField;
}>(({ id, original, edited, disabled, activeLabel, inactiveLabel, onChange }) => (
    <Select
        value={edited ?? original}
        style={{ width: '100%' }}
        status={edited !== undefined ? 'warning' : undefined}
        disabled={disabled}
        onChange={(val) => onChange(id, 'status', val, original)}
        options={[
            { value: 'active', label: activeLabel },
            { value: 'inactive', label: inactiveLabel },
        ]}
    />
));

const TAB_SUFFIXES: Record<string, string> = {
    durable: ' (D2C)',
    perishable: ' (D2C)',
};

const formatTemplateTypeName = (type: string): string => {
    return type
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const QUESTION_TYPE_LABELS: Record<number, string> = {
    1: 'Likert 7',
    2: 'Likert 5',
    3: 'Yes/No',
    4: 'Rating 10',
    5: 'Text',
    6: 'Multiple Choice',
    7: 'NPS',
};

const CATEGORY_COLORS: Record<string, string> = {
    Loyalty: 'blue',
    'Product Quality': 'green',
    'Brand Awareness': 'orange',
    'Brand Association': 'purple',
    'Customer Satisfaction': 'cyan',
    'Purchase Intention': 'magenta',
    'Value for Money': 'gold',
    'Brand Trust': 'geekblue',
    'Overall Brand Equity': 'red',
    'Store Visuals': 'lime',
    'Staff Interaction': 'volcano',
    'Brand Attachment': 'pink',
    Touchpoints: 'yellow',
};

const TemplateTypeTab: React.FC<{
    templateType: string;
    isBuiltIn: boolean;
    onDelete: () => void;
}> = ({ templateType, isBuiltIn, onDelete }) => {
    const { user } = useAuth();
    const intl = useIntl();
    const [searchText, setSearchText] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string | undefined>(
        undefined,
    );
    const [scoringTypeFilter, setScoringTypeFilter] = useState<
        string | undefined
    >(undefined);
    const [edits, setEdits] = useState<Record<number, TemplateEdit>>({});

    const { data, isLoading } = useGetTemplatesByTypeQuery({
        jwtToken: user?.jwtToken || '',
        templateType,
    });
    const [updateTemplate, { isLoading: isSaving }] =
        useUpdateQuestionTemplateMutation();

    const templates = data?.data || [];
    const inUse = data?.inUse ?? true;
    const surveyCount = data?.surveyCount ?? 0;
    // Templates can only be edited when the type is not referenced by any survey.
    const editable = !isLoading && !inUse;

    const setField = useCallback<SetField>((id, field, value, original) => {
        setEdits((prev) => {
            const next = { ...prev };
            const current: TemplateEdit = { ...next[id] };
            if (value === original) {
                delete current[field];
            } else {
                current[field] = value as any;
            }
            if (Object.keys(current).length === 0) {
                delete next[id];
            } else {
                next[id] = current;
            }
            return next;
        });
    }, []);

    const changedIds = Object.keys(edits).map(Number);
    const hasChanges = changedIds.length > 0;

    const handleDiscard = () => setEdits({});

    // Column headers here are the contract for the clone wizard's spreadsheet
    // import — keep them in sync with CloneTemplateTypeWizard's parser.
    const handleExportCsv = () => {
        const rows = templates.map((t) => ({
            Code: t.code,
            'Statement Text': t.text,
            Category: t.category,
            'Question Type':
                QUESTION_TYPE_LABELS[t.questionTypeId] ||
                `Type ${t.questionTypeId}`,
            'Scoring Type': t.scoringType,
            Core: t.isCore ? 'Yes' : 'No',
            Required: t.isRequired ? 'Yes' : 'No',
            Status: t.status,
            Order: t.displayOrder,
        }));
        // This always produced a CSV (bookType: 'csv'), so it never needed a
        // spreadsheet library at all. toCsv also neutralises leading = + - @,
        // which Excel would otherwise execute as a formula on open.
        downloadTextFile(
            `statement-templates-${templateType}.csv`,
            toCsv(rows),
        );
    };

    const handleSave = async () => {
        try {
            await Promise.all(
                changedIds.map((id) =>
                    updateTemplate({
                        jwtToken: user?.jwtToken || '',
                        id,
                        payload: edits[id],
                    }).unwrap(),
                ),
            );
            message.success(
                intl.formatMessage(
                    {
                        id: 'admin.templates.saveSuccess',
                        defaultMessage:
                            '{count, plural, one {# template updated} other {# templates updated}}',
                    },
                    { count: changedIds.length },
                ),
            );
            setEdits({});
        } catch (error: any) {
            message.error(
                error?.data?.message ||
                    intl.formatMessage({
                        id: 'admin.templates.saveFailed',
                        defaultMessage: 'Failed to save changes',
                    }),
            );
        }
    };

    const categories = useMemo(() => {
        return Array.from(new Set(templates.map((t) => t.category))).sort();
    }, [templates]);

    const scoringTypes = useMemo(() => {
        return Array.from(new Set(templates.map((t) => t.scoringType))).sort();
    }, [templates]);

    const filteredTemplates = useMemo(() => {
        return templates.filter((t) => {
            if (
                searchText &&
                !t.text.toLowerCase().includes(searchText.toLowerCase()) &&
                !t.code.toLowerCase().includes(searchText.toLowerCase())
            ) {
                return false;
            }
            if (categoryFilter && t.category !== categoryFilter) {
                return false;
            }
            return !(scoringTypeFilter && t.scoringType !== scoringTypeFilter);

        });
    }, [templates, searchText, categoryFilter, scoringTypeFilter]);

    const columns: ColumnsType<IQuestionTemplate> = [
        {
            title: intl.formatMessage({ id: 'admin.templates.code', defaultMessage: 'Code' }),
            dataIndex: 'code',
            key: 'code',
            width: 100,
            sorter: (a, b) => a.code.localeCompare(b.code),
            render: (code: string) => (
                <Text code style={{ fontSize: 12 }}>
                    {code}
                </Text>
            ),
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.text', defaultMessage: 'Statement Text' }),
            dataIndex: 'text',
            key: 'text',
            ellipsis: editable ? undefined : { showTitle: false },
            render: (text: string, record) => {
                if (editable) {
                    return (
                        <EditableTextCell
                            id={record.id}
                            original={text}
                            edited={edits[record.id]?.text}
                            disabled={isSaving}
                            onChange={setField}
                        />
                    );
                }
                return (
                    <Tooltip title={text} placement="topLeft">
                        <span>{text}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.category', defaultMessage: 'Category' }),
            dataIndex: 'category',
            key: 'category',
            width: 160,
            sorter: (a, b) => a.category.localeCompare(b.category),
            render: (category: string) => (
                <Tag color={CATEGORY_COLORS[category] || 'default'}>
                    {category}
                </Tag>
            ),
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.questionType', defaultMessage: 'Question Type' }),
            dataIndex: 'questionTypeId',
            key: 'questionTypeId',
            width: 120,
            render: (id: number) => (
                <Tag>{QUESTION_TYPE_LABELS[id] || `Type ${id}`}</Tag>
            ),
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.scoringType', defaultMessage: 'Scoring Type' }),
            dataIndex: 'scoringType',
            key: 'scoringType',
            width: editable ? 210 : 180,
            render: (scoringType: string, record) => {
                if (editable) {
                    return (
                        <EditableScoringCell
                            id={record.id}
                            original={scoringType}
                            edited={edits[record.id]?.scoringType}
                            disabled={isSaving}
                            onChange={setField}
                        />
                    );
                }
                return <Tag>{scoringType?.replace(/_/g, ' ')}</Tag>;
            },
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.core', defaultMessage: 'Core' }),
            dataIndex: 'isCore',
            key: 'isCore',
            width: 70,
            align: 'center' as const,
            sorter: (a, b) => Number(a.isCore) - Number(b.isCore),
            render: (isCore: boolean) =>
                isCore ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                ) : (
                    <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
                ),
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.required', defaultMessage: 'Required' }),
            dataIndex: 'isRequired',
            key: 'isRequired',
            width: 80,
            align: 'center' as const,
            render: (isRequired: boolean) =>
                isRequired ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                ) : (
                    <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
                ),
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.status', defaultMessage: 'Status' }),
            dataIndex: 'status',
            key: 'status',
            width: editable ? 130 : 100,
            align: 'center' as const,
            render: (status: string, record) => {
                if (editable) {
                    return (
                        <EditableStatusCell
                            id={record.id}
                            original={status}
                            edited={edits[record.id]?.status}
                            disabled={isSaving}
                            activeLabel={intl.formatMessage({
                                id: 'admin.templates.statusActive',
                                defaultMessage: 'Active',
                            })}
                            inactiveLabel={intl.formatMessage({
                                id: 'admin.templates.statusInactive',
                                defaultMessage: 'Inactive',
                            })}
                            onChange={setField}
                        />
                    );
                }
                return status === 'active' ? (
                    <Tag color="green">active</Tag>
                ) : (
                    <Tag
                        color="red"
                        style={{ fontWeight: 700, textTransform: 'uppercase' }}
                    >
                        inactive
                    </Tag>
                );
            },
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.order', defaultMessage: 'Order' }),
            dataIndex: 'displayOrder',
            key: 'displayOrder',
            width: 70,
            align: 'center' as const,
            sorter: (a, b) => a.displayOrder - b.displayOrder,
        },
    ];

    return (
        <div>
            <Row
                justify="space-between"
                align="middle"
                style={{ marginBottom: 16 }}
            >
                <Col>
                    <Space>
                        <Input
                            placeholder={intl.formatMessage({ id: 'admin.templates.search', defaultMessage: 'Search by code or text...' })}
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 280 }}
                            allowClear
                        />
                        <Select
                            placeholder={intl.formatMessage({ id: 'admin.templates.filterCategory', defaultMessage: 'Category' })}
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                            allowClear
                            style={{ width: 180 }}
                        >
                            {categories.map((cat) => (
                                <Select.Option key={cat} value={cat}>
                                    {cat}
                                </Select.Option>
                            ))}
                        </Select>
                        <Select
                            placeholder={intl.formatMessage({ id: 'admin.templates.filterScoring', defaultMessage: 'Scoring Type' })}
                            value={scoringTypeFilter}
                            onChange={setScoringTypeFilter}
                            allowClear
                            style={{ width: 200 }}
                        >
                            {scoringTypes.map((st) => (
                                <Select.Option key={st} value={st}>
                                    {st?.replace(/_/g, ' ')}
                                </Select.Option>
                            ))}
                        </Select>
                    </Space>
                </Col>
                <Col>
                    <Space>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleExportCsv}
                            disabled={isLoading || templates.length === 0}
                        >
                            <FormattedMessage
                                id="admin.templates.downloadCsv"
                                defaultMessage="Download CSV"
                            />
                        </Button>
                        {editable && hasChanges && (
                            <>
                                <Button
                                    icon={<UndoOutlined />}
                                    onClick={handleDiscard}
                                    disabled={isSaving}
                                >
                                    <FormattedMessage
                                        id="admin.templates.discard"
                                        defaultMessage="Discard"
                                    />
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    loading={isSaving}
                                    onClick={handleSave}
                                >
                                    <FormattedMessage
                                        id="admin.templates.saveChanges"
                                        defaultMessage="Save Changes ({count})"
                                        values={{ count: changedIds.length }}
                                    />
                                </Button>
                            </>
                        )}
                        {!isBuiltIn && (
                            <Tooltip
                                title={
                                    inUse
                                        ? intl.formatMessage(
                                              { id: 'admin.templates.cannotDelete', defaultMessage: 'Cannot delete: used by {count} surveys' },
                                              { count: surveyCount },
                                          )
                                        : undefined
                                }
                            >
                                <Button
                                    danger
                                    icon={
                                        inUse ? <LockOutlined /> : <DeleteOutlined />
                                    }
                                    disabled={inUse}
                                    onClick={onDelete}
                                >
                                    <FormattedMessage
                                        id="admin.templates.deleteType"
                                        defaultMessage="Delete Template Type"
                                    />
                                </Button>
                            </Tooltip>
                        )}
                    </Space>
                </Col>
            </Row>

            {!isLoading && inUse && (
                <Alert
                    type="info"
                    showIcon
                    icon={<LockOutlined />}
                    style={{ marginBottom: 16 }}
                    message={intl.formatMessage(
                        {
                            id: 'admin.templates.lockedHint',
                            defaultMessage:
                                'This template type is in use by {count, plural, one {# survey} other {# surveys}}, so its statements cannot be edited.',
                        },
                        { count: surveyCount },
                    )}
                />
            )}

            <Table
                columns={columns}
                dataSource={filteredTemplates}
                loading={isLoading}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 900 }}
                rowClassName={(record) =>
                    !editable && record.status === 'inactive'
                        ? 'inactive-template-row'
                        : ''
                }
            />
            <style>{`
                .inactive-template-row {
                    background: #fff1f0 !important;
                }
                .inactive-template-row td {
                    text-decoration: line-through !important;
                    color: #cf1322 !important;
                    opacity: 0.65;
                }
                .inactive-template-row td .ant-tag {
                    text-decoration: none !important;
                }
            `}</style>
        </div>
    );
};

export const AdminStatementTemplatesPage: React.FC = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const [cloneWizardOpen, setCloneWizardOpen] = useState(false);
    const [wiringDrawerOpen, setWiringDrawerOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

    const { data: templateTypesData, isLoading: typesLoading } =
        useGetTemplateTypesQuery({
            jwtToken: user?.jwtToken || '',
        });

    // Registry rows drive the built-in badge/lock (replaces a hardcoded list).
    const { data: registryRows } = useGetAdminQuestionTemplateTypesQuery({
        jwtToken: user?.jwtToken || '',
    });
    const builtInSlugs = useMemo(
        () =>
            new Set(
                (registryRows || [])
                    .filter((r) => r.isBuiltIn)
                    .map((r) => r.slug),
            ),
        [registryRows],
    );

    const [deleteTemplateType] = useDeleteTemplateTypeMutation();

    const templateTypes = templateTypesData?.data || [];

    const handleDeleteType = (type: string) => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'admin.templates.confirmDelete', defaultMessage: 'Delete Template Type?' }),
            icon: <ExclamationCircleOutlined />,
            content: intl.formatMessage(
                { id: 'admin.templates.confirmDeleteContent', defaultMessage: 'This will permanently delete all statement templates for type "{type}". This action cannot be undone.' },
                { type: formatTemplateTypeName(type) },
            ),
            okText: intl.formatMessage({ id: 'common.delete', defaultMessage: 'Delete' }),
            okType: 'danger',
            onOk: async () => {
                try {
                    await deleteTemplateType({
                        jwtToken: user?.jwtToken || '',
                        templateType: type,
                    }).unwrap();
                    message.success(
                        intl.formatMessage({ id: 'admin.templates.deleteSuccess', defaultMessage: 'Template type deleted successfully' }),
                    );
                    if (activeTab === type) {
                        setActiveTab(templateTypes[0]);
                    }
                } catch (error: any) {
                    message.error(
                        error?.data?.message ||
                            intl.formatMessage({ id: 'admin.templates.deleteFailed', defaultMessage: 'Failed to delete template type' }),
                    );
                }
            },
        });
    };

    if (typesLoading) {
        return (
            <div style={{ textAlign: 'center', padding: 80 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Row
                justify="space-between"
                align="middle"
                style={{ marginBottom: 24 }}
            >
                <Col>
                    <Title level={3} style={{ margin: 0 }}>
                        <FileTextOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage
                            id="admin.templates.title"
                            defaultMessage="Statement Templates"
                        />
                    </Title>
                </Col>
                <Col>
                    <Space>
                        <Button
                            icon={<ApartmentOutlined />}
                            onClick={() => setWiringDrawerOpen(true)}
                        >
                            <FormattedMessage
                                id="admin.templates.typeWiring"
                                defaultMessage="Type Wiring"
                            />
                        </Button>
                        <Button
                            type="primary"
                            icon={<CopyOutlined />}
                            onClick={() => setCloneWizardOpen(true)}
                        >
                            <FormattedMessage
                                id="admin.templates.createNew"
                                defaultMessage="Create New Template"
                            />
                        </Button>
                    </Space>
                </Col>
            </Row>

            {templateTypes.length === 0 ? (
                <Empty
                    description={
                        <FormattedMessage
                            id="admin.templates.noTemplates"
                            defaultMessage="No statement templates found"
                        />
                    }
                />
            ) : (
                <Tabs
                    activeKey={activeTab || templateTypes[0]}
                    onChange={setActiveTab}
                    items={templateTypes.map((type) => ({
                        key: type,
                        label: (
                            <span>
                                {formatTemplateTypeName(type)}{TAB_SUFFIXES[type] || ''}
                                {builtInSlugs.has(type) && (
                                    <Badge
                                        status="processing"
                                        style={{ marginLeft: 6 }}
                                    />
                                )}
                            </span>
                        ),
                        children: (
                            <TemplateTypeTab
                                templateType={type}
                                isBuiltIn={builtInSlugs.has(type)}
                                onDelete={() => handleDeleteType(type)}
                            />
                        ),
                    }))}
                />
            )}

            <CloneTemplateTypeWizard
                open={cloneWizardOpen}
                onClose={() => setCloneWizardOpen(false)}
                templateTypes={templateTypes}
            />

            <Drawer
                title={
                    <FormattedMessage
                        id="admin.templates.typeWiringTitle"
                        defaultMessage="Template Type Wiring"
                    />
                }
                width={880}
                open={wiringDrawerOpen}
                onClose={() => setWiringDrawerOpen(false)}
                destroyOnClose
            >
                <TemplateTypeRegistryPanel />
            </Drawer>
        </div>
    );
};
